import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { property } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { html, nothing } from 'lit'

import { ariaElements, delegateEvents, refCollection } from '#utils/directives'
import styles, { boolean } from '../form-boolean/lfds-boolean.css.ts'
import { LfuiElement } from '#utils/element'
import { WithFocus, compose } from '#utils/mixins'

/**
 * @name LfdsRadio
 * @tag lfds-radio
 * @status beta
 *
 * @description
 * Individual radio button. Always use inside `lfds-radio-group`, which owns
 * form participation and keyboard navigation. Mutual exclusion among radios
 * with the same `name` is handled via the static `groups` map.
 */
export default class LfdsRadio extends compose(LfuiElement, WithFocus) {
	static styles = [LfuiElement.styles, styles, boolean]
	static events = ['change']

	/** Tracks all connected radios keyed by name, for mutual exclusion. */
	static groups: Map<string, Set<LfdsRadio>> = new Map()

	protected delegatedEvents = ['change', 'focus', 'blur', 'focusin', 'focusout']

	@property() label!: string
	@property() name: string
	@property() value: string
	@property() autocomplete: 'on' | 'off'
	@property({ type: Boolean }) checked: boolean = false
	@property({ type: Boolean }) required: boolean
	@property({ type: Boolean }) disabled: boolean
	@property({ type: Boolean }) invalid: boolean

	@property({ attribute: 'sr-label' }) srLabel: string
	@property({ attribute: 'sr-labelledby' }) srLabelledby: string
	@property({ attribute: 'sr-describedby' }) srDescribedby: string
	@property({ attribute: 'sr-controls' }) srControls: string

	#input = createRef<HTMLInputElement>()
	#labels = refCollection()
	#descriptions = refCollection()

	connectedCallback() {
		super.connectedCallback()
		if (!this.hasAttribute('role')) this.setAttribute('role', 'none')
		this.#addToGroup()
		this.addEventListener('click', this.#handleHostClick)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('click', this.#handleHostClick)
		LfdsRadio.groups.get(this.name)?.delete(this)
		if (LfdsRadio.groups.get(this.name)?.size === 0) {
			LfdsRadio.groups.delete(this.name)
		}
	}

	updated(changed) {
		super.updated(changed)
		if (changed.has('name')) {
			const oldName = changed.get('name') as string
			if (oldName) {
				LfdsRadio.groups.get(oldName)?.delete(this)
				if (LfdsRadio.groups.get(oldName)?.size === 0) {
					LfdsRadio.groups.delete(oldName)
				}
			}
			this.#addToGroup()
		}
	}

	#addToGroup() {
		if (this.name) {
			if (!LfdsRadio.groups.has(this.name)) {
				LfdsRadio.groups.set(this.name, new Set())
			}
			LfdsRadio.groups.get(this.name)!.add(this)
		}
	}

	#handleHostClick = (event: Event) => {
		if (event.composedPath()[0] === this) {
			this.#input.value?.click()
		}
	}

	#handleChange = (_event: Event) => {
		if (this.name && !LfdsRadio.groups.get(this.name)?.has(this)) {
			this.#addToGroup()
		}

		// Uncheck all siblings in the same group
		for (const radio of LfdsRadio.groups.get(this.name) || []) {
			if (radio !== this) radio.checked = false
		}

		this.checked = true
		this.invalid = false
	}

	render() {
		return html`
			<label
				class=${classMap({
					wrapper: true,
					'focus-visible': this.focusVisible
				})}
			>
				<div
					class=${classMap({
						control: true,
						checked: this.checked,
						invalid: this.invalid
					})}
				>
					<input
						id="input"
						type="radio"
						class="boolean radio"
						aria-invalid=${this.invalid ? 'true' : (nothing as any)}
						aria-label=${ifDefined(this.srLabel)}
						value=${ifDefined(this.value)}
						name=${ifDefined(this.name)}
						autocomplete=${ifDefined(this.autocomplete)}
						?required=${this.required}
						?disabled=${this.disabled}
						.checked=${this.checked}
						${ref(this.#input)}
						${delegateEvents(this.delegatedEvents, {
							before: { change: this.#handleChange }
						})}
						${this.#labels.bind('ariaLabelledByElements')}
						${this.#descriptions.bind('ariaDescribedByElements')}
						${ariaElements({
							describedBy: this.srDescribedby,
							labelledBy: this.srLabelledby,
							controls: this.srControls
						})}
					/>
					<span class="radio-dot"></span>
				</div>
				${this.label || this.querySelector('[slot="label"]')
					? html`<span class="label" ${this.#labels.add()}>
							<slot name="label">${this.label}</slot>
						</span>`
					: nothing}
			</label>
		`
	}
}
